import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import { AppContext } from 'views/App/context';
import errorHelper from 'helpers/errorHelper';
import { translate as t } from 'hooks/useTranslations';
import { Template } from '../../_types';
import { cleanTemplateForApi, transformApiResponseToFrontend } from '../../_data_sources/useTemplateAutoSave';
import { ServerErrorResponse } from 'data_sources/constants';
import { useNavigationHandlers } from './useNavigationHandlers';
import { validateTemplate } from '../../_utils/templateValidation';

export const useTemplateActions = (
  template: Template,
  templateEditable: Template,
  setIsSaving: (saving: boolean) => void,
  updateTemplate: any,
  publishTemplate: (
    templateId: string,
    payload: { changeNote?: string; reviewWorkflowId?: number; updatedBy?: string },
    callback?: (data: Template) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => Promise<void>,
  resetTemplate: any,
  triggerAutoSave: (template: Template, note: string) => void,
  enforceAutoSave: (template: Template, note: string) => Promise<Template>,
  isReorderMode: boolean,
  setIsReorderMode: (mode: boolean) => void,
  setTemplate: (template: Template) => void,
  setTemplateEditable: React.Dispatch<React.SetStateAction<Template>>,
  clearInactiveFieldIds: () => void,
  setInactiveFieldIds: (ids: Set<number>) => void,
  clearDuplicateFieldIds: () => void,
  setDuplicateFieldIds: (ids: Set<number>) => void,
  clearDuplicateSectionIds: () => void,
  setDuplicateSectionIds: (ids: Set<number>) => void,
  clearEmptySectionIds: () => void,
  setEmptySectionIds: (ids: Set<number>) => void,
  clearRemovableChildFieldIds: () => void,
  setRemovableChildFieldIds: (ids: Set<number>) => void,
  setIsTemplateDisabled: (value: boolean) => void,
  isLexipolTemplate: boolean,
  isLexipolStaff: boolean
) => {
  const { handlePublish: handlePublishNavigation } = useNavigationHandlers(template);
  const queryClient = useQueryClient();
  const { currentOrganizationId: orgId } = useContext(AppContext);

  const saveTemplate = useCallback(
    async (changeNote?: string): Promise<void> => {
      setIsSaving(true);

      const templateForApi = cleanTemplateForApi(templateEditable);

      return new Promise((resolve, reject) => {
        updateTemplate(
          templateForApi as Template,
          changeNote,
          (updatedTemplate: Template) => {
            // Transform the API response back to frontend format
            const transformedTemplate = transformApiResponseToFrontend(updatedTemplate);
            // Update cache directly instead of invalidating to avoid refetch
            queryClient.setQueryData(['template', updatedTemplate.id], transformedTemplate);
            // Only invalidate templates list for structural changes like add/remove
            queryClient.invalidateQueries(['templates']);

            setIsSaving(false);
            resolve();
          },
          (error: ServerErrorResponse) => {
            setIsSaving(false);
            reject(error);
          }
        );
      });
    },
    [templateEditable, updateTemplate, queryClient]
  );

  const handlePublishTemplate = useCallback(
    async (changeNote?: string, reviewWorkflowId?: number, updatedBy?: string): Promise<void> => {
      if (!template.id) {
        throw new Error('Cannot publish a template without an ID');
      }

      // Use centralized validation utility
      const { inactiveFieldIds, duplicateFieldIds, duplicateSectionIds, emptySectionIds, removableChildFieldIds } =
        validateTemplate(templateEditable);

      const hasInactiveFields = inactiveFieldIds.size > 0;
      const hasDuplicateSections = duplicateSectionIds.size > 0;
      const hasDuplicateFields = duplicateFieldIds.size > 0;
      const hasEmptySections = emptySectionIds.size > 0;
      const hasRemovableChildFields = removableChildFieldIds.size > 0;

      // Set all validation states at once so UI highlights all issues
      if (hasInactiveFields) setInactiveFieldIds(inactiveFieldIds);
      if (hasDuplicateSections) setDuplicateSectionIds(duplicateSectionIds);
      if (hasDuplicateFields) setDuplicateFieldIds(duplicateFieldIds);
      if (hasEmptySections) setEmptySectionIds(emptySectionIds);
      if (hasRemovableChildFields) setRemovableChildFieldIds(removableChildFieldIds);

      // Show warning and prevent publish if any validation issues exist
      if (
        hasInactiveFields ||
        hasDuplicateSections ||
        hasDuplicateFields ||
        hasEmptySections ||
        hasRemovableChildFields
      ) {
        const messages = [];
        if (hasInactiveFields) {
          messages.push(t('performanceReporting.templates.details.actions.inactiveFieldsWarning'));
        }
        if (hasDuplicateSections) {
          messages.push(t('performanceReporting.templates.details.actions.duplicateSectionsWarning'));
        }
        if (hasDuplicateFields) {
          messages.push(t('performanceReporting.templates.details.actions.duplicateFieldsWarning'));
        }
        if (hasEmptySections) {
          messages.push(t('performanceReporting.templates.details.actions.emptySectionsWarning'));
        }
        if (hasRemovableChildFields) {
          const templateMode = isLexipolTemplate ? 'lexipol' : 'agency';
          messages.push(
            t(`performanceReporting.templates.builder.${templateMode}.lockedSection.removableFieldsWarning`)
          );
        }

        enqueueSnackbar(messages.join(' '), {
          variant: 'warning',
          autoHideDuration: 8000,
        });

        return;
      }

      setIsTemplateDisabled(true);
      setIsSaving(true);

      try {
        // Enforce auto-save before publishing to prevent race condition
        const savedTemplate = await enforceAutoSave(templateEditable, 'Pre-publish save');
        setTemplate(savedTemplate);
        const updatedByValue = updatedBy || templateEditable.updatedBy || 'current-user';
        const payload = isLexipolStaff
          ? { updatedBy: updatedByValue }
          : {
              changeNote: changeNote || '',
              reviewWorkflowId: reviewWorkflowId,
              updatedBy: updatedByValue,
            };

        if (!isLexipolStaff && !reviewWorkflowId) {
          throw new Error('Review workflow is required to publish an agency template.');
        }
        return new Promise((resolve, reject) => {
          publishTemplate(
            savedTemplate.id.toString(),
            payload,
            (publishedTemplate: Template) => {
              const transformedTemplate = transformApiResponseToFrontend(publishedTemplate);
              setTemplate(transformedTemplate);
              setIsTemplateDisabled(false);
              setIsSaving(false);
              clearInactiveFieldIds();
              clearDuplicateFieldIds();
              clearDuplicateSectionIds();
              clearEmptySectionIds();
              clearRemovableChildFieldIds();
              enqueueSnackbar(t('performanceReporting.templates.details.actions.publishSuccess'), {
                variant: 'success',
              });

              queryClient.invalidateQueries(['templates']);
              queryClient.invalidateQueries(['template', savedTemplate.id]);
              queryClient.invalidateQueries(['templateVersions', savedTemplate.id]);

              if (savedTemplate.parentTemplateId) {
                queryClient.invalidateQueries(['childTemplates', savedTemplate.parentTemplateId]);
              }

              handlePublishNavigation(transformedTemplate);
              resolve();
            },
            (error: ServerErrorResponse) => {
              setIsTemplateDisabled(false);
              setIsSaving(false);
              errorHelper.enqueueErrors(error);
              reject(error);
            }
          );
        });
      } catch (error) {
        setIsTemplateDisabled(false);
        setIsSaving(false);
        console.error('Error saving before publish:', error);
        enqueueSnackbar('Failed to save changes before publishing. Please try again.', { variant: 'error' });
        throw error;
      }
    },
    [
      template.id,
      publishTemplate,
      templateEditable,
      enforceAutoSave,
      setTemplate,
      enqueueSnackbar,
      t,
      queryClient,
      setInactiveFieldIds,
      clearInactiveFieldIds,
      setDuplicateFieldIds,
      clearDuplicateFieldIds,
      setDuplicateSectionIds,
      clearDuplicateSectionIds,
      setEmptySectionIds,
      clearEmptySectionIds,
      setRemovableChildFieldIds,
      clearRemovableChildFieldIds,
      handlePublishNavigation,
      setIsTemplateDisabled,
      isLexipolTemplate,
      isLexipolStaff,
    ]
  );

  const handleResetTemplate = useCallback(async (): Promise<void> => {
    if (!template.id) {
      throw new Error('Cannot reset a template without an ID');
    }

    setIsTemplateDisabled(true);
    setIsSaving(true);

    return new Promise((resolve, reject) => {
      resetTemplate(
        template.id.toString(),
        (resetTemplateData: Template) => {
          const transformedTemplate = transformApiResponseToFrontend(resetTemplateData);
          setTemplate(transformedTemplate);
          setTemplateEditable(transformedTemplate);
          setIsTemplateDisabled(false);
          setIsSaving(false);
          clearInactiveFieldIds();
          clearDuplicateFieldIds();
          clearDuplicateSectionIds();
          clearEmptySectionIds();
          clearRemovableChildFieldIds();
          enqueueSnackbar(t('performanceReporting.templates.details.actions.resetSuccess'), { variant: 'success' });

          queryClient.setQueryData(['template', template.id], transformedTemplate);
          queryClient.invalidateQueries(['templates']);
          queryClient.invalidateQueries(['templateVersions', template.id]);

          if (template.parentTemplateId) {
            queryClient.invalidateQueries(['childTemplates', template.parentTemplateId]);
          }

          resolve();
        },
        (error: ServerErrorResponse) => {
          setIsTemplateDisabled(false);
          setIsSaving(false);
          // Check if error message indicates template was already published
          const errorMessage =
            error.errorDetails?.[0]?.message ||
            error.errorMessage ||
            error.message ||
            t('performanceReporting.templates.details.actions.resetError');
          enqueueSnackbar(errorMessage, { variant: 'error' });
          errorHelper.enqueueErrors(error);
          reject(error);
        }
      );
    });
  }, [
    template.id,
    template.parentTemplateId,
    resetTemplate,
    setTemplate,
    setTemplateEditable,
    enqueueSnackbar,
    t,
    queryClient,
    clearInactiveFieldIds,
    clearDuplicateFieldIds,
    clearDuplicateSectionIds,
    clearEmptySectionIds,
    clearRemovableChildFieldIds,
    setIsTemplateDisabled,
  ]);

  const toggleReorderMode = useCallback(async () => {
    const wasInReorderMode = isReorderMode;

    if (!wasInReorderMode) {
      setIsReorderMode(true); // effect will snapshot collapse + force all collapsed
    } else {
      setIsReorderMode(false);

      try {
        triggerAutoSave(templateEditable, 'Section order updated');
      } catch (error) {
        console.error('Error saving template after reordering sections:', error);
      }
    }
  }, [isReorderMode, triggerAutoSave, templateEditable, setIsReorderMode]);

  return {
    saveTemplate,
    publishTemplate: handlePublishTemplate,
    resetTemplate: handleResetTemplate,
    toggleReorderMode,
  };
};
